import { useTranslation } from "react-i18next"

import { useFairUseConfigForm } from "./fair-use-config-form-context"
import { FairUseConfigFormLoading } from "./fair-use-config-form-provider"
import { FairUseDelegationPanel } from "./fair-use-delegation-panel"
import { FairUseLimitsPanel } from "./fair-use-limits-panel"
import { fairUseLimitsUpdate } from "./fair-use-settings-utils"
import { isFairUseDowngradeModelResolved, readFairUseDowngradeModelOptions } from "./fair-use-utils"
import { DelegationModeControl } from "../delegation-mode-control"

/**
 * Settings — what happens as a user spends, and who may change it.
 *
 * The limits are the delegable half of fair use: whether it acts at all, the
 * four steps it acts at, and what a fallback turn runs as. Nothing here is an
 * input to an invoice, which is why the Save sends the delegable fields and
 * only those — a commercial field in the same payload would answer 403 and
 * apply nothing, including the step the admin came here to move.
 *
 * The lead approval allowance is NOT on this panel although it rides on the
 * same payload: it is money a lead may hand out, so it belongs next to the
 * lead roster on Users & licences.
 */
export function FairUseTabSettings() {
  const { t } = useTranslation()
  const { data, dirty, form, isLoading, isPlatformAdmin, save, savingPanel, updateForm } =
    useFairUseConfigForm()

  if (isLoading || !form || !data) return <FairUseConfigFormLoading />

  return (
    <div className="space-y-8">
      <FairUseLimitsPanel
        form={form}
        isDirty={dirty.limits}
        isSaving={savingPanel === "limits"}
        modelOptions={readFairUseDowngradeModelOptions(data)}
        // The allowance lives with the lead roster on Users & licences; sending
        // it from here too would give one amount two Save buttons.
        showLeadAllowance={false}
        // The platform's suffix-tolerant verdict describes the model it last
        // READ, so it is withdrawn the moment the admin edits the field.
        isModelResolvedByPlatform={
          form.downgradeModel === data.config.downgrade_model &&
          isFairUseDowngradeModelResolved(data)
        }
        onChange={(patch) => updateForm("limits", patch)}
        onSave={() => save("limits", fairUseLimitsUpdate(form))}
      />

      <div className="space-y-4 border-t pt-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-sm font-semibold">
            {t("adminDashboard.fairUse.settings.delegation.title", "Customer admin access")}
          </h3>
          <DelegationModeControl capability="fair_use" />
        </div>
        <FairUseDelegationPanel isPlatformAdmin={isPlatformAdmin} />
      </div>
    </div>
  )
}
